import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Loader2, Cloud, Server, Eye, EyeOff } from "lucide-react";
import CloudImagePicker from "@/components/admin/CloudImagePicker";
import { getSettings, saveSettings } from "@/lib/cloudApi";
import { isSupabaseReady } from "@/lib/supabase";
import { ICONIC_SLOTS, normaliseIconicMoments, positionToObjectPosition } from "@/data/iconicMoments";

export default function AdminSettings() {
  const [s, setS] = useState({
    logoUrl: "", heroImage: "", heroVideo: "", founderImage: "",
    introText: "", aboutText: "",
  });
  const [busy, setBusy]     = useState(false);
  const [loading, setLoading] = useState(true);
  const [iconic, setIconic] = useState(() => normaliseIconicMoments([]));

  useEffect(() => {
    (async () => {
      try {
        const data = await getSettings();
        if (data) setS(prev => ({ ...prev, ...data }));
        setIconic(normaliseIconicMoments(data?.homeIconicMoments));
      } finally { setLoading(false); }
    })();
  }, []);

  const set = (k, v) => setS(x => ({ ...x, [k]: v }));

  // Update one slot field by slot number
  const setSlot = (slot, key, value) =>
    setIconic(list => list.map(it => (it.slot === slot ? { ...it, [key]: value } : it)));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      // Normalise before saving: a slot counts as enabled only if it
      // has an image. Stored as the homeIconicMoments JSON array.
      const cleanIconic = iconic.map(it => ({
        slot: it.slot,
        imageUrl: (it.imageUrl || "").trim(),
        alt: it.alt || "",
        caption: it.caption || "",
        position: it.position || "center",
        fit: it.fit || "cover",
        enabled: Boolean(it.enabled && (it.imageUrl || "").trim()),
      }));
      await saveSettings({ ...s, homeIconicMoments: cleanIconic });
      toast.success("Settings saved — live website will update on next load");
    } catch { toast.error("Save failed"); }
    finally { setBusy(false); }
  };

  const inp = "w-full bg-transparent border-b border-[#C99A45]/30 focus:border-[#C99A45] outline-none py-2 text-sm";

  if (loading) return <div className="p-10 text-[#F7F1E8]/60">Loading...</div>;

  return (
    <div className="p-10 max-w-4xl" data-testid="admin-settings">
      {/* Mode badge */}
      <div className="mb-6 flex items-center gap-2 text-xs">
        {isSupabaseReady() ? (
          <span className="flex items-center gap-1 bg-emerald-900/30 text-emerald-400 border border-emerald-700/40 px-3 py-1">
            <Cloud size={11} /> Supabase Cloud — changes go live instantly
          </span>
        ) : (
          <span className="flex items-center gap-1 bg-yellow-900/30 text-yellow-400 border border-yellow-700/40 px-3 py-1">
            <Server size={11} /> Local backend only — set Supabase env vars for production
          </span>
        )}
      </div>

      <div className="eyebrow text-[#C99A45]">Manage</div>
      <h1 className="font-serif-display text-5xl mt-3">Site Settings</h1>
      <div className="gold-divider my-6" />

      <form onSubmit={save} className="space-y-10 mt-8">
        <section className="space-y-4">
          <h2 className="font-serif-display text-2xl text-[#C99A45]">Branding</h2>
          <CloudImagePicker value={s.logoUrl} onChange={v => set("logoUrl", v)}
            label="Logo (PNG with transparent background)" testId="s-logo" folder="branding" />
        </section>

        <section className="space-y-4">
          <h2 className="font-serif-display text-2xl text-[#C99A45]">Home Hero</h2>
          <CloudImagePicker value={s.heroImage} onChange={v => set("heroImage", v)}
            label="Hero Background Image" testId="s-hero-image" folder="hero" />
          <div>
            <label className="eyebrow text-[10px] text-[#C99A45]/80">Hero Background Video URL (overrides image)</label>
            <input value={s.heroVideo || ""} onChange={e => set("heroVideo", e.target.value)}
              placeholder="https://...mp4" className={inp} data-testid="s-hero-video" />
          </div>
          <div>
            <label className="eyebrow text-[10px] text-[#C99A45]/80">Brand Intro Text (home)</label>
            <textarea rows={3} value={s.introText || ""}
              onChange={e => set("introText", e.target.value)}
              className={`${inp} resize-none`} data-testid="s-intro" />
          </div>
        </section>

        <section className="space-y-4">
          <h2 className="font-serif-display text-2xl text-[#C99A45]">About</h2>
          <CloudImagePicker value={s.founderImage} onChange={v => set("founderImage", v)}
            label="Founder / Studio Portrait" testId="s-founder" folder="about" />
          <div>
            <label className="eyebrow text-[10px] text-[#C99A45]/80">About Page Text</label>
            <textarea rows={6} value={s.aboutText || ""}
              onChange={e => set("aboutText", e.target.value)}
              className={`${inp} resize-none`} data-testid="s-about" />
          </div>
        </section>

        {/* ── HOME PAGE · ICONIC MOMENTS ─────────────────────── */}
        <section className="space-y-5">
          <div>
            <h2 className="font-serif-display text-2xl text-[#C99A45]">Home Page · Iconic Moments</h2>
            <p className="text-xs text-[#F7F1E8]/50 mt-2 max-w-2xl leading-relaxed">
              Curated mosaic on the Home page. These are the <strong>only</strong> images shown there —
              wedding, pre-wedding and client-gallery albums are never pulled in automatically.
              Fill the slots you want; empty slots are skipped. Slots render in the exact order below.
            </p>
          </div>

          {/* Live slot preview */}
          <div>
            <label className="eyebrow text-[10px] text-[#C99A45]/80 block mb-2">Live Preview (slot order)</label>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 max-w-2xl">
              {iconic.map((it) => (
                <div key={it.slot}
                  className="relative bg-[#151515] border border-[#C99A45]/15 overflow-hidden"
                  style={{ aspectRatio: "3/4" }}>
                  {it.imageUrl ? (
                    <img src={it.imageUrl} alt={it.alt || ""}
                      className="w-full h-full"
                      style={{
                        objectFit: it.fit === "contain" ? "contain" : "cover",
                        objectPosition: positionToObjectPosition(it.position),
                        opacity: it.enabled ? 1 : 0.35,
                      }} />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[#C99A45]/30 text-[10px]">
                      {it.slot}
                    </div>
                  )}
                  <span className="absolute top-1 left-1 text-[9px] bg-black/70 text-[#C99A45] px-1">{it.slot}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Slot editors */}
          <div className="space-y-4">
            {iconic.map((it) => {
              const blueprint = ICONIC_SLOTS.find(b => b.slot === it.slot);
              return (
                <div key={it.slot}
                  className="border border-[#C99A45]/15 p-4 bg-[#111]/40"
                  data-testid={`iconic-slot-${it.slot}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="eyebrow text-[11px] text-[#C99A45]">
                      Slot {it.slot} · <span className="text-[#F7F1E8]/55">{blueprint?.role}</span>
                    </div>
                    <button type="button"
                      onClick={() => setSlot(it.slot, "enabled", !it.enabled)}
                      disabled={!it.imageUrl}
                      className={`flex items-center gap-1 text-[10px] eyebrow px-2 py-1 border transition ${
                        it.enabled
                          ? "border-emerald-600/50 text-emerald-400"
                          : "border-[#C99A45]/25 text-[#F7F1E8]/40"
                      } ${!it.imageUrl ? "opacity-40 cursor-not-allowed" : ""}`}>
                      {it.enabled ? <Eye size={11} /> : <EyeOff size={11} />}
                      {it.enabled ? "Shown" : "Hidden"}
                    </button>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    {/* image */}
                    <CloudImagePicker
                      value={it.imageUrl}
                      onChange={(v) => {
                        setSlot(it.slot, "imageUrl", v);
                        if (v) setSlot(it.slot, "enabled", true);
                      }}
                      label="Image"
                      testId={`iconic-img-${it.slot}`}
                      folder="iconic"
                      previewClassName={it.fit === "contain" ? "object-contain" : "object-cover"}
                    />

                    {/* meta */}
                    <div className="space-y-3">
                      <div>
                        <label className="eyebrow text-[10px] text-[#C99A45]/80">Alt text</label>
                        <input value={it.alt}
                          onChange={e => setSlot(it.slot, "alt", e.target.value)}
                          className={inp} placeholder="Describe the photo" />
                      </div>
                      <div>
                        <label className="eyebrow text-[10px] text-[#C99A45]/80">Caption (optional)</label>
                        <input value={it.caption}
                          onChange={e => setSlot(it.slot, "caption", e.target.value)}
                          className={inp} placeholder="Shown on hover" />
                      </div>
                      <div className="flex gap-4">
                        <div className="flex-1">
                          <label className="eyebrow text-[10px] text-[#C99A45]/80">Position</label>
                          <select value={it.position}
                            onChange={e => setSlot(it.slot, "position", e.target.value)}
                            className={`${inp} text-[#F7F1E8] [&>option]:text-black`}>
                            <option value="top">Top</option>
                            <option value="center">Center</option>
                            <option value="bottom">Bottom</option>
                          </select>
                        </div>
                        <div className="flex-1">
                          <label className="eyebrow text-[10px] text-[#C99A45]/80">Fit</label>
                          <select value={it.fit}
                            onChange={e => setSlot(it.slot, "fit", e.target.value)}
                            className={`${inp} text-[#F7F1E8] [&>option]:text-black`}>
                            <option value="cover">Cover (fill)</option>
                            <option value="contain">Contain (full image)</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <button type="submit" disabled={busy} className="btn-gold flex items-center gap-2" data-testid="s-save">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
          Save Settings
        </button>
      </form>
    </div>
  );
}
